import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Card } from "react-bootstrap";
import api from "../../api/axios";
import { useAppToast } from "../Common/Toast/AppToastProvider";

const LIMIT = 20;

const ReviewsAdminView = () => {
  const { showToast } = useAppToast();
  const business = JSON.parse(localStorage.getItem("business") || "null");
  const businessId = business?.id;

  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [approvedTotal, setApprovedTotal] = useState(0);
  const [rejectedTotal, setRejectedTotal] = useState(0);
  const [pendingOffset, setPendingOffset] = useState(0);
  const [approvedOffset, setApprovedOffset] = useState(0);
  const [rejectedOffset, setRejectedOffset] = useState(0);

  const fetchByStatus = async (status, offset = 0) => {
    const res = await api.get("/reviews/admin", {
      params: { business_id: businessId, status, limit: LIMIT, offset },
    });
    return { items: res.data?.items || [], total: Number(res.data?.total || 0) };
  };

  const refreshAll = async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [p, a, r] = await Promise.all([
        fetchByStatus("pending", pendingOffset),
        fetchByStatus("approved", approvedOffset),
        fetchByStatus("rejected", rejectedOffset),
      ]);
      setPending(p.items);
      setApproved(a.items);
      setRejected(r.items);
      setPendingTotal(p.total);
      setApprovedTotal(a.total);
      setRejectedTotal(r.total);
    } catch (e) {
      console.error("Error cargando reviews:", e);
      showToast("No se pudieron cargar las reseñas.", "danger", "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, [businessId, pendingOffset, approvedOffset, rejectedOffset]);

  const setStatus = async (id, nextStatus) => {
    const prev = [...pending];
    setPending((rows) => rows.filter((r) => r.id !== id));
    try {
      await api.patch(`/reviews/${id}/status`, { status: nextStatus });
      showToast(`Reseña ${nextStatus === "approved" ? "aprobada" : "rechazada"}.`, "success", "Listo");
      await refreshAll();
    } catch (e) {
      console.error("No se pudo actualizar estado:", e);
      setPending(prev);
      showToast("No se pudo actualizar la reseña.", "danger", "Error");
    }
  };

  const TableBlock = ({ title, rows, total, offset, onPrev, onNext, withActions = false }) => (
    <>
      <h4 className="mt-4">{title}</h4>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Usuario</th>
            <th>Benefit</th>
            <th>Rating</th>
            <th>Comentario</th>
            <th>Fecha</th>
            {withActions ? <th style={{ width: 200 }}>Accion</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={withActions ? 7 : 6} className="text-center text-muted py-3">
                Sin registros
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td>{offset + i + 1}</td>
              <td>{r.display_name || `user:${r.user_id}`}</td>
              <td>{r.benefit_id}</td>
              <td>{`${r.rating}/5`}</td>
              <td>
                <div className="text-truncate" style={{ maxWidth: 280 }}>
                  {r.comment || <em className="text-muted">-</em>}
                </div>
              </td>
              <td>{new Date(r.created_at).toLocaleString()}</td>
              {withActions ? (
                <td>
                  <Button size="sm" variant="success" onClick={() => setStatus(r.id, "approved")}>
                    Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="ms-2"
                    onClick={() => setStatus(r.id, "rejected")}
                  >
                    Rechazar
                  </Button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          {total === 0 ? "0" : `${offset + 1}-${Math.min(offset + LIMIT, total)}`} de {total}
        </small>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" disabled={offset <= 0} onClick={onPrev}>
            Anterior
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={offset + LIMIT >= total}
            onClick={onNext}
          >
            Siguiente
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div>
      <h2 className="mb-3">Gestion de Reseñas</h2>

      <Card className="p-3 mb-3">
        <Row className="align-items-center">
          <Col>
            <strong>Negocio:</strong> {business?.name || businessId || "-"}
          </Col>
          <Col className="text-end">
            <Button variant="outline-secondary" onClick={refreshAll} disabled={loading}>
              {loading ? "Actualizando..." : "Actualizar"}
            </Button>
          </Col>
        </Row>
      </Card>

      <TableBlock
        title="Pendientes de moderacion"
        rows={pending}
        total={pendingTotal}
        offset={pendingOffset}
        onPrev={() => setPendingOffset((v) => Math.max(0, v - LIMIT))}
        onNext={() => setPendingOffset((v) => v + LIMIT)}
        withActions
      />

      <TableBlock
        title="Aprobadas"
        rows={approved}
        total={approvedTotal}
        offset={approvedOffset}
        onPrev={() => setApprovedOffset((v) => Math.max(0, v - LIMIT))}
        onNext={() => setApprovedOffset((v) => v + LIMIT)}
      />

      <TableBlock
        title="Rechazadas"
        rows={rejected}
        total={rejectedTotal}
        offset={rejectedOffset}
        onPrev={() => setRejectedOffset((v) => Math.max(0, v - LIMIT))}
        onNext={() => setRejectedOffset((v) => v + LIMIT)}
      />
    </div>
  );
};

export default ReviewsAdminView;
